// ═══════════════════════════════════════════════════════════════
// PublicInspectionReport — the inspection as the customer sees it.
//
// This is the deliverable that separates a mobile mechanic from a
// guy with a Venmo link: colour-coded findings, in plain language,
// with the photo of the worn part sitting right next to the line
// that says it needs replacing.
//
// Reads only from the frozen `inspectionReport` snapshot on the
// share doc — never from the live inspection.
// ═══════════════════════════════════════════════════════════════
import React from 'react';
import { ClipboardCheck, Gauge } from 'lucide-react';
import { statusMeta } from './lib/inspection-helpers.js';

function formatCompletedAt(ts) {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function PhotoGrid({ urls }) {
  if (!urls?.length) return null;
  return (
    <div className="public-inspection-photos">
      {urls.map((url, i) => (
        <a
          key={url + i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="public-inspection-photo-link"
        >
          <img src={url} alt={`Inspection photo ${i + 1}`} loading="lazy" />
        </a>
      ))}
    </div>
  );
}

function StatusSection({ section }) {
  return (
    <div className="public-inspection-section">
      <h4 className="public-inspection-section-title">{section.label}</h4>
      <ul className="public-inspection-list">
        {section.items.map((item) => {
          const meta = statusMeta(item.status);
          return (
            <li key={item.key} className={`public-inspection-row ${meta.cls}`}>
              <div className="public-inspection-row-head">
                <span className="public-inspection-label">{item.label}</span>
                <span className={`public-inspection-badge ${meta.cls}`}>{meta.label}</span>
              </div>
              {item.note && <p className="public-inspection-note">{item.note}</p>}
              <PhotoGrid urls={item.photoUrls} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TireSection({ section }) {
  const { corners = [], suggestions = [], note, photoUrls, tpms } = section;
  return (
    <div className="public-inspection-section">
      <h4 className="public-inspection-section-title">{section.label}</h4>

      {corners.length > 0 && (
        <div className="public-tire-table-wrap">
          <table className="public-tire-table">
            <thead>
              <tr>
                <th scope="col">Tire</th>
                <th scope="col">Tread</th>
                <th scope="col">PSI</th>
                <th scope="col">Condition</th>
              </tr>
            </thead>
            <tbody>
              {corners.map((c) => {
                const meta = statusMeta(c.status);
                const psi = c.pressureAfter ?? c.pressureBefore;
                return (
                  <tr key={c.key} className={meta.cls}>
                    <th scope="row">{c.label}</th>
                    <td className="mono">{c.tread != null ? `${c.tread}/32"` : '—'}</td>
                    <td className="mono">
                      {psi != null ? psi : '—'}
                      {c.pressureBefore != null && c.pressureAfter != null &&
                        c.pressureBefore !== c.pressureAfter && (
                          <span className="muted small public-tire-was">was {c.pressureBefore}</span>
                        )}
                    </td>
                    <td>
                      {/* Short label here: the legend above the report
                          spells the scale out, and the full sentence
                          pushes this column off a phone screen. */}
                      <span className={`public-inspection-badge ${meta.cls}`}>{meta.short}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tpms && (
        <p className="public-inspection-note">
          TPMS warning system: {statusMeta(tpms).label}
        </p>
      )}

      {suggestions.length > 0 && (
        <p className="public-tire-suggestions">
          <strong>Suggested tire service:</strong> {suggestions.join(', ')}
        </p>
      )}

      {note && <p className="public-inspection-note">{note}</p>}
      <PhotoGrid urls={photoUrls} />
    </div>
  );
}

export default function PublicInspectionReport({ report }) {
  const sections = Array.isArray(report?.sections) ? report.sections : [];
  if (sections.length === 0) return null;

  return (
    <div className="public-inspection">
      <h3 className="public-inspection-title">
        <ClipboardCheck size={16} /> {report.templateName || 'Vehicle inspection'}
      </h3>

      <p className="muted small public-inspection-stamp">
        {report.completedAt && <>Completed {formatCompletedAt(report.completedAt)}</>}
        {report.completedAt && report.odometer != null && ' · '}
        {report.odometer != null && (
          <>
            <Gauge size={11} style={{ verticalAlign: '-1px' }} />{' '}
            {Number(report.odometer).toLocaleString()} mi
          </>
        )}
      </p>

      <div className="public-inspection-legend">
        <span className="public-inspection-badge insp-good">Checked and OK</span>
        <span className="public-inspection-badge insp-attention">May require attention</span>
        <span className="public-inspection-badge insp-replace">Requires immediate attention</span>
      </div>

      {sections.map((section) =>
        section.kind === 'tires'
          ? <TireSection key={section.key} section={section} />
          : <StatusSection key={section.key} section={section} />
      )}

      {report.comments && (
        <div className="public-inspection-comments">
          <p className="public-section-label">Comments</p>
          <p className="public-inspection-note">{report.comments}</p>
        </div>
      )}
    </div>
  );
}
